import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, ArrowLeft, RefreshCw, ChevronDown, ChevronRight,
  Check, Lock, Unlock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RolePermissions() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const [expandedRoles, setExpandedRoles] = useState({});

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/roles/permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPermissions(data.permissions || []);
        // Expand Admin by default
        setExpandedRoles({ 'Admin': true });
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const toggleRole = (role) => {
    setExpandedRoles(prev => ({ ...prev, [role]: !prev[role] }));
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'Admin': 'bg-red-100 text-red-700 border-red-300',
      'Accountant': 'bg-green-100 text-green-700 border-green-300',
      'HR Manager': 'bg-blue-100 text-blue-700 border-blue-300',
      'Teacher': 'bg-purple-100 text-purple-700 border-purple-300',
      'Librarian': 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'Front Desk': 'bg-pink-100 text-pink-700 border-pink-300',
      'Exam Controller': 'bg-orange-100 text-orange-700 border-orange-300',
      'Admission Officer': 'bg-cyan-100 text-cyan-700 border-cyan-300',
      'Inventory Manager': 'bg-teal-100 text-teal-700 border-teal-300',
      'Hostel In-charge': 'bg-indigo-100 text-indigo-700 border-indigo-300',
      'Transport Manager': 'bg-lime-100 text-lime-700 border-lime-300'
    };
    return colors[role] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Role Permissions</h1>
            <p className="text-gray-600 mt-1">View which modules each role can access</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchPermissions}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Understanding Permissions:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Each role has access to specific modules</li>
                <li>Staff can have multiple roles - they get combined access</li>
                <li>Admin has access to all modules</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Permissions List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {permissions.map((role) => (
            <Card key={role.role} className="overflow-hidden">
              <div
                onClick={() => toggleRole(role.role)}
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
              >
                <div className="flex items-center gap-4">
                  <div className={`px-3 py-1 rounded-lg border ${getRoleBadgeColor(role.role)}`}>
                    <span className="font-medium">{role.name}</span>
                  </div>
                  <p className="text-gray-600">{role.description}</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                    {role.module_count === 'All' ? '✨ All Modules' : `${role.module_count} modules`}
                  </span>
                  {expandedRoles[role.role] ? (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </div>
              
              {expandedRoles[role.role] && (
                <CardContent className="border-t bg-gray-50 p-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">Accessible Modules:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {role.modules.map((module) => (
                      <div
                        key={module.key}
                        className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200"
                      >
                        <div className="mt-0.5">
                          {module.key === '*' ? (
                            <Unlock className="h-4 w-4 text-green-500" />
                          ) : (
                            <Check className="h-4 w-4 text-green-500" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{module.name}</p>
                          <p className="text-xs text-gray-500">{module.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Legend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Legend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-green-500" />
              <span>Module Access</span>
            </div>
            <div className="flex items-center gap-2">
              <Unlock className="h-4 w-4 text-green-500" />
              <span>Full System Access</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-red-500" />
              <span>No Access</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
